import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Col, Empty, Space, Typography } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import pmvService from '@/services/pmv.service';

const { Text } = Typography;

/**
 * Fuel Control Tower — attention-first operational fuel intelligence.
 * Inventory stock authority unchanged. Telemetry ≠ issued quantity.
 */
const PmvFuelControlTowerPage: React.FC = () => {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['pmv-fuel-control-tower'],
    queryFn: () => pmvService.getFuelControlTower(),
    staleTime: 30_000,
  });
  const d = q.data;
  const litresToday = d?.fuel_attributed_today?.litres;

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Fuel Control Tower"
        subtitle="Authoritative issues vs observed evidence — advisory reconciliation only"
        primaryActions={
          <Space wrap>
            <Button onClick={() => navigate('/asset/pmv/fuel')}>Fuel Register</Button>
            <Button onClick={() => navigate('/asset/pmv/fuel/reconciliation')}>Reconciliation</Button>
            <Button onClick={() => navigate('/asset/pmv/fuel/attention')}>Attention</Button>
            <Button onClick={() => navigate('/asset/pmv/fuel/sources')}>Sources / Bowsers</Button>
            <Button onClick={() => navigate('/asset/pmv/control-tower')}>Fleet Command Center</Button>
          </Space>
        }
      />

      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="Authority boundary"
        description={
          d?.note ||
          'Operational fuel intelligence only. Inventory remains stock authority. Telemetry is evidence, not an Inventory issue.'
        }
      />

      {!q.isLoading && (d?.open_attention ?? 0) === 0 && (d?.open_reconciliation ?? 0) === 0 ? (
        <Empty
          style={{ marginBottom: 16 }}
          description="No fuel reconciliation is required right now."
        />
      ) : null}

      <ControlTowerSectionCard zone="attention" title="Requires attention">
        <ControlTowerMetricStrip zone="attention" title="Attention">
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile
              title="Open fuel attention"
              count={d?.open_attention ?? 0}
              urgency={(d?.open_attention ?? 0) > 0 ? 'attention' : 'neutral'}
              footerLink={{
                label: 'Open queue',
                onClick: () => navigate('/asset/pmv/fuel/attention'),
              }}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile
              title="Open reconciliation"
              count={d?.open_reconciliation ?? 0}
              urgency={(d?.open_reconciliation ?? 0) > 0 ? 'attention' : 'neutral'}
              footerLink={{
                label: 'Open reconciliation',
                onClick: () => navigate('/asset/pmv/fuel/reconciliation'),
              }}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile
              title="Variance cases"
              count={d?.variance_count ?? 0}
              urgency={(d?.variance_count ?? 0) > 0 ? 'attention' : 'neutral'}
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile
              title="Open bowser sessions"
              count={d?.open_delivery_sessions ?? 0}
              footerLink={{
                label: 'Sources / bowsers',
                onClick: () => navigate('/asset/pmv/fuel/sources'),
              }}
            />
          </Col>
        </ControlTowerMetricStrip>
      </ControlTowerSectionCard>

      <ControlTowerSectionCard zone="metricsOrders" title="Today (operational attribution)">
        <ControlTowerMetricStrip zone="metricsOrders" title="Today">
          <Col xs={12} sm={8} md={6}>
            <ControlTowerMetricTile
              title="Fuel events today"
              count={d?.fuel_attributed_today?.count ?? 0}
            />
          </Col>
          <Col xs={12} sm={12} md={8}>
            <div style={{ padding: 12 }}>
              <Text type="secondary">Litres attributed today</Text>
              <div style={{ fontSize: 22, fontWeight: 600 }}>
                {litresToday == null ? 'Unavailable' : `${Number(litresToday).toFixed(1)} L`}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Missing litres are never shown as 0
              </Text>
            </div>
          </Col>
        </ControlTowerMetricStrip>
      </ControlTowerSectionCard>
    </WorkspaceLayout>
  );
};

export default PmvFuelControlTowerPage;
